import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHeadset, FaSignOutAlt } from 'react-icons/fa';
import { MdOutlineAccountCircle } from 'react-icons/md';
import { IoMdNotificationsOutline } from 'react-icons/io';
import { BiTrip } from "react-icons/bi";
import { IoChevronBackCircleOutline } from "react-icons/io5";
import { Helmet } from 'react-helmet';
import logo from '../static/images/ss-original.png';
import { fetchNotifications } from '../../Services/ApiCallsServices/HomeDashboardApi';
import { useAuth } from '../../context/AuthContext';

const CustomerPage = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [title, setTitle] = useState('Driver Dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, logout } = useAuth();

  const email = auth.user?.email;
  const firstName = auth.user?.first_name ?? '';
  const lastName = auth.user?.last_name ?? '';
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotificationsHandler = async () => {
    try {
      const data = await fetchNotifications(email);
      if (data?.notifications && Array.isArray(data.notifications)) {
        const unreadCount = data.notifications.filter(notif => !notif.is_read).length;
        setUnreadCount(unreadCount);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    fetchNotificationsHandler();
  }, [email]);

  useEffect(() => {
    const titleMap = {
      '/login': 'Home',
      '/profile': 'Profile',
      '/support': 'Support',
      '/dashboard': 'Request Trip'
    };
    setTitle(titleMap[location.pathname] || 'Welcome to Storage Sprint');
  }, [location.pathname]);

  const handleProfileClick = () => navigate('/profile');
  const handleRequestTrip = () => navigate('/dashboard');
  const handleTripsClick = () => navigate('/notifications');
  const handleSupportClick = () => navigate('/support');

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.clear();
      navigate('/login');
    } catch (error) {
      alert('An error occurred during logout. Please try again later.');
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      {/* Top Navigation Bar */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                onClick={toggleMenu}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Logo */}
              <div className="flex-shrink-0 flex items-center">
                <img className="h-12 w-auto" src={logo} alt="Storage Sprint" />
              </div>
            </div>

            {/* User controls */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleTripsClick}
                className="relative p-1 rounded-full text-gray-500 hover:text-gray-900 focus:outline-none"
              >
                <IoMdNotificationsOutline className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>

              <div className="hidden md:flex items-center space-x-2">
                <span className="text-sm font-medium text-yellow-500">
                  {firstName} {lastName}
                </span>
                <button
                  onClick={handleLogout}
                  className="p-1 rounded-full text-yellow-500 hover:text-yellow-600 focus:outline-none"
                  title="Logout"
                >
                  <FaSignOutAlt className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar and Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <div
          className={`${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:static inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out`}
        >
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between px-4 border-b border-gray-200">
              <img className="h-8 w-auto md:hidden" src={logo} alt="Storage Sprint" />
              <button
                onClick={toggleMenu}
                className="md:hidden text-gray-500 hover:text-gray-900 focus:outline-none"
              >
                <IoChevronBackCircleOutline className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-2 py-4 space-y-1">
              <button
                onClick={handleRequestTrip}
                className="group flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-gray-700 hover:bg-yellow-50 hover:text-yellow-600"
              >
                <BiTrip className="mr-3 h-5 w-5 text-yellow-500 group-hover:text-yellow-600" />
                Request Trip
              </button>
              <button
                onClick={handleTripsClick}
                className="group flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-gray-700 hover:bg-yellow-50 hover:text-yellow-600"
              >
                <IoMdNotificationsOutline className="mr-3 h-5 w-5 text-yellow-500 group-hover:text-yellow-600" />
                Notifications
              </button>
              <button
                onClick={handleSupportClick}
                className="group flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-gray-700 hover:bg-yellow-50 hover:text-yellow-600"
              >
                <FaHeadset className="mr-3 h-5 w-5 text-yellow-500 group-hover:text-yellow-600" />
                Support
              </button>
              <button
                onClick={handleProfileClick}
                className="group flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-gray-700 hover:bg-yellow-50 hover:text-yellow-600"
              >
                <MdOutlineAccountCircle className="mr-3 h-5 w-5 text-yellow-500 group-hover:text-yellow-600" />
                Profile
              </button>
            </nav>

            {/* Mobile User Info */}
            <div className="md:hidden px-4 py-4 border-t border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MdOutlineAccountCircle className="h-10 w-10 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {firstName} {lastName}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-sm font-medium text-yellow-500 hover:text-red-700"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overlay for mobile sidebar */}
        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 md:hidden"
            onClick={toggleMenu}
          ></div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto focus:outline-none bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

CustomerPage.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CustomerPage;